import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hrmsLogin } from '../../lib/hrmsApi';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { company } from '../../mock';
import { Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';

const HrmsLogin = () => {
  const { toast } = useToast();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await hrmsLogin(email, password);
      localStorage.setItem('hrms_token', res.token);
      localStorage.setItem('hrms_user', JSON.stringify(res.user));
      toast({ title: `Welcome, ${res.user.name || res.user.email}` });
      nav(res.user.must_reset ? '/hrms/reset-password' : '/hrms');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Login failed';
      toast({ title: 'Login failed', description: msg });
    } finally { setLoading(false); }
  };

  const signInWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/hrms';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-5">
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ backgroundImage: 'radial-gradient(700px 300px at 20% 20%, rgba(249,115,22,0.35), transparent 60%), radial-gradient(700px 300px at 90% 80%, rgba(59,130,246,0.3), transparent 60%)' }} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-8">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-orange-400"><ArrowLeft className="h-3.5 w-3.5" /> Back to website</Link>
        <div className="mt-4 flex items-center gap-3">
          <img src={company.logo} alt="Hipro" className="h-10 w-10 rounded" />
          <div>
            <div className="font-bold">Hipro HRMS</div>
            <div className="text-xs text-slate-400">Internal portal</div>
          </div>
        </div>
        <h1 className="mt-6 text-2xl font-extrabold">Sign in</h1>
        <p className="text-sm text-slate-300 mt-1">Recruitment, employees and HR in one place.</p>
        <div className="mt-5 space-y-3">
          <div><Label className="text-slate-200">Work email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="h-11 mt-1 bg-white text-slate-900" placeholder="you@hiprotechnologies.com" data-testid="hrms-login-email" /></div>
          <div><Label className="text-slate-200">Password</Label><Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" className="h-11 mt-1 bg-white text-slate-900" placeholder="••••••••" data-testid="hrms-login-password" /></div>
        </div>
        <Button type="submit" disabled={loading} className="mt-6 w-full h-11 bg-orange-500 hover:bg-orange-600" data-testid="hrms-login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" /> Sign in</>}
        </Button>

        <div className="mt-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-white/10" />
          <div className="text-[11px] text-slate-400 uppercase tracking-widest">or</div>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <button type="button" onClick={signInWithGoogle} className="mt-4 w-full h-11 rounded-md bg-white text-slate-900 font-semibold flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors" data-testid="hrms-google-signin">
          <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
          Sign in with Google
        </button>
        <p className="mt-3 text-[11px] text-slate-400 text-center">Only pre-invited HR staff can sign in with Google.</p>
      </form>
    </div>
  );
};

export default HrmsLogin;
